import React, { Component } from 'react';
import { compose, graphql, withApollo } from 'react-apollo';
import { Table, Button, Breadcrumb, Input } from 'antd';
import { Link } from 'react-router-dom';
import AntIcon from '../shared/components/AntIcon';
import moment from 'moment';
import _ from 'lodash';

import { ListReleasesQuery, UpdateReleaseMutation } from './queries';

class List extends Component {
  constructor(props) {
    super(props);

    this.state = {
      searchText: '',
      filtered: false,
    };

    this.handlePriorityUpdate = this.handlePriorityUpdate.bind(this);
    this.tableData = this.tableData.bind(this);
  }

  componentDidMount() {
    const { loading, fetchMore, Releases } = this.props.data;
    if (!loading && Releases) {
      const updatedSince = _(Releases).map('updatedAt').max();
      fetchMore({
        variables: {
          filter: {
            "updatedAt": { "$gt": updatedSince },
          },
        },
        updateQuery: (previousResult, { fetchMoreResult, _variables }) => {
          const newIds = fetchMoreResult.Releases.map(s => s._id);
          const Releases = _(previousResult.Releases)
            .reject(s => newIds.includes(s._id))
            .concat(fetchMoreResult.Releases)
            .orderBy(['date', 'orderPriority'], ['asc', 'desc'])
            .value();
          return { Releases };
        },
      })
    }
  }

  handlePriorityUpdate(id, oldPriority, change) {
    const { client } = this.props;
    const priority = (oldPriority || 0) + (change === 'up' ? 1 : -1);
    client.mutate({
      mutation: UpdateReleaseMutation,
      variables: { id, update: { orderPriority: priority } },
    })
      .catch(e => alert(`Failed to change ordering: ${e.message}.`));
  }

  tableData(data) {
    const { searchText, filtered } = this.state;
    if (!filtered) return data;

    const reg = new RegExp(`\\b${searchText}\\b`, 'gi');
    return data.filter(sneaker => sneaker.searchString.match(reg));
  }

  render() {
    const { loading, error, Releases } = this.props.data;
    if (error) return `Error: ${error}`;

    const { searchText, filtered } = this.state;

    const makeButtonProps = (id, priority, change) => {
      return {
        key: change, type: `arrow-${change}`,
        style: { cursor: 'pointer' },
        onClick: () => this.handlePriorityUpdate(id, priority, change),
      }
    }

    const imageStyle = { objectFit: 'cover', height: 100, width: 160 };

    const columns = [
      {
        title: '',
        dataIndex: 'orderPriority',
        width: 100,
        render: (priority, { _id }) => {
          return ([
            <AntIcon {...makeButtonProps(_id, priority, 'up')} />,
            <span key="priority">{priority > 10000 ? priority - 100000 : priority}</span>,
            <AntIcon {...makeButtonProps(_id, priority, 'down')} />,
          ]);
        },
      },
      {
        title: 'Date',
        dataIndex: 'date',
        width: 160,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => {
          if (a.date !== b.date) {
            return a.date.localeCompare(b.date);
          }
          const aPriority = a.orderPriority || 0;
          const bPriority = b.orderPriority || 0;
          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }
          return a.createdAt.localeCompare(b.createdAt);
        },
        render: (date, record) => moment.utc(date).format(record.hasUnknownDay ? "MMM Y" : "ll"),
      },
      {
        title: 'Image',
        key: 'thumbnail',
        dataIndex: 'imageUrls',
        width: 160,
        render: (links, { title }) => <img alt={title} style={imageStyle} src={(links || [])[0]} />,
      },
      {
        title: 'Title',
        dataIndex: 'title',
        width: 280,
        sorter: (a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()),
        render: (text, record) => <Link to={`/releases/${record._id}/edit`}>{text}</Link>,
      },
      {
        title: 'Color',
        dataIndex: 'colorway',
        width: 280,
        render: (colorway, { nickname }) => colorway || nickname,
      },
      {
        title: 'Price',
        dataIndex: 'price',
        width: 90,
        render: (text) => _.isString(text) ? text : `$${text}`,
      },
    ];

    const FilteredReleases = _.reject(Releases, 'isDeleted');
    const tableData = this.tableData(FilteredReleases);

    const thisMonth = moment().utc().startOf('day').toISOString();
    const initialIndex = tableData.findIndex(({ date }) => date >= thisMonth);

    return (
      <div id="releases-list">
        <Breadcrumb>
          <Breadcrumb.Item>Releases</Breadcrumb.Item>
        </Breadcrumb>

        <div className="container">
          <h4>
            Manage Releases
            <span className="is-right" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Input.Search
                placeholder="Search name"
                value={searchText}
                onChange={e => this.setState({ searchText: e.target.value })}
                onSearch={value => this.setState({ searchText: value || '', filtered: !!(value && value.trim()) })}
                style={{ width: 180 }}
                allowClear
              />
              <Button>
                <Link to="/releases/new"><AntIcon type="plus" /> New Release</Link>
              </Button>
            </span>
          </h4>

          <Table
            scroll={{ x: 1000 }}
            key={initialIndex === -1 ? 'loading-releases-table' : 'releases-table'}
            loading={Releases ? false : loading}
            rowClassName={(s) => s.featured ? 'featured' : ''}
            columns={columns}
            dataSource={tableData}
            rowKey='_id'
            pagination={{
              pageSize: 30,
              showSizeChanger: true,
              pageSizeOptions: ['30', '60', '100', '200'],
              showTotal: (total) => `Total ${total} items`,
              defaultCurrent: Math.trunc(initialIndex / 30) + 1,
            }}
          />
        </div>
      </div>
    );
  }
}

export default compose(
  graphql(ListReleasesQuery),
  withApollo,
)(List);
